import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import PrimeVue from 'primevue/config';
import Lara from '@primeuix/themes/lara';
import { definePreset } from '@primeuix/themes';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import FocusTrap from 'primevue/focustrap';
import Tooltip from 'primevue/tooltip';
import ConfirmationService from 'primevue/confirmationservice';

import { useAuthStore } from '@/stores/auth';
import { useCompaniesStore } from '@/stores/companies';
import { useEmployeesStore } from '@/stores/employees';
import { useStructuresStore } from '@/stores/structures';

import 'primeicons/primeicons.css';
import "@/assets/css/WavingFlagAnimation.css";
import 'primeflex/primeflex.css';
import '@/assets/css/custom.css'

const app = createApp(App);
const pinia = createPinia();
const ImabPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

document.documentElement.classList.add('app-dark');

['employeesList', 'employeeNames', 'companiesList', 'allRoster', 'allRosters', 'allLeaves', 'attendanceData'].forEach(key => {
  localStorage.removeItem(key);
});

app.use(pinia);
app.use(PrimeVue, {
  theme: {
    preset: ImabPreset,
    options: {
      darkModeSelector: '.app-dark',
      cssLayer: false,
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);
app.directive('focustrap', FocusTrap);
app.directive('tooltip', Tooltip);
app.component('PrimeToast', Toast);

const authStore = useAuthStore();
const companiesStore = useCompaniesStore();
const employeesStore = useEmployeesStore();
const structuresStore = useStructuresStore();

watch(
  () => [authStore.isInitialized, authStore.isAuthenticated],
  async ([isInitialized, isAuthenticated]) => {
    if (!isInitialized) {
      return;
    }

    if (!isAuthenticated) {
      companiesStore.clearCompanies();
      employeesStore.clearEmployees();
      structuresStore.clearStructures();
      return;
    }

    try {
      await Promise.all([
        companiesStore.ensureCompanies(),
        employeesStore.ensureEmployees(),
      ]);
    } catch (error) {
      console.error('Failed to initialize application data:', error);
    }
  },
  { immediate: true }
);

// Initialize auth and mount app
authStore.initializeAuth()
  .then(() => {
    app.use(router);

    // Setup navigation guard
    router.beforeEach(async (to, from, next) => {
      if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        next('/login');
      } else if (to.path === '/login' && authStore.isAuthenticated) {
        next('/');
      } else {
        next();
      }
    });

    // Error handling for resource loading
    app.config.errorHandler = (err, vm, info) => {
      console.error('Vue Error:', err, info);
    };

    window.addEventListener('error', (event) => {
      if (event.target && (event.target.tagName === 'LINK' || event.target.tagName === 'SCRIPT')) {
        console.error('Resource loading error:', event.target.src || event.target.href);
      }
    });

    // Mount the app
    app.mount('#app');
  })
  .catch(error => {
    console.error('Failed to initialize auth:', error);
  });
